// @flow

import { fileExtentsion } from '@ta/input.drop-zone';
import { type ApolloClient } from 'react-apollo';
import Logging from '@ta/common.logging';
import restoreExif from './exifRestorer';
import { type MediaType, supportedImageTypes, supportedVideoTypes } from './types';
import PhotoDetails from './PhotoDetails.gql';

/**
 * Utility to convert base64 into a Uint8Array
 */
export const base64ToUint8Array = (base64: string): Uint8Array => {
  const image = atob(base64.split(',')[1]);
  const imgBuffer = [];
  let i = 0;
  while (i < image.length) {
    imgBuffer.push(image.charCodeAt(i));
    i += 1;
  }
  return new Uint8Array(imgBuffer);
};

/**
 * Utility to crop an image
 */
export const cropImageToJpegUrl = (
  image: Image,
  mimeType: string,
  x: number,
  y: number,
  width: number,
  height: number,
): string => {
  /* eslint-disable-next-line no-restricted-globals */
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  context.drawImage(image, x, y, width, height, 0, 0, width, height);

  if (mimeType === 'image/png') {
    return canvas.toDataURL();
  }

  return canvas.toDataURL('image/jpeg');
};

/**
 * Utility to rotate an image
 */
const rotateImage = (
  imageUrl: string,
  degrees: number,
  mimeType: string,
  callback: (dataUrl: string) => void,
) => {
  const image = new Image();
  image.onload = () => {
    /* eslint-disable-next-line no-restricted-globals */
    const canvas = document.createElement('canvas');

    // if we are rotating to right(90) or left(270) canvas size should be opposite of image size
    canvas.width = Math.abs(degrees % 180) === 90 ? image.height : image.width;
    canvas.height = Math.abs(degrees % 180) === 90 ? image.width : image.height;
    const context = canvas.getContext('2d');

    // save the unrotated context of the canvas so we can restore it later
    // the alternative is to untranslate & unrotate after drawing
    context.save();
    // move to the center of the canvas
    context.translate(canvas.width / 2, canvas.height / 2);
    // rotate the canvas to the specified degrees
    context.rotate((degrees * Math.PI) / 180);
    // draw the image
    // since the context is rotated, the image will be rotated also
    context.drawImage(image, -image.width / 2, -image.height / 2);
    // we’re done with the rotating so restore the unrotated context
    context.restore();

    callback(canvas.toDataURL(mimeType));
  };
  image.src = imageUrl;
};

/**
 * This will return the base64 representation of the image data given the file path
 */
export const getImageDataURL = (imageUrl: string, callback: (dataUrl: string) => void) => {
  const image = new Image();

  image.onload = () => {
    /* eslint-disable-next-line no-restricted-globals */
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0);
    context.save();
    callback(canvas.toDataURL());
  };
  image.src = imageUrl;
};

/**
 * This will return the degrees the image should be rotated to correct it
 */
export const orientationValueToDegrees = (orientationValue: number): number => {
  if (orientationValue === 1) {
    return 0;
  }
  if (orientationValue === 8) {
    return 270;
  }
  if (orientationValue === 3) {
    return 180;
  }
  if (orientationValue === 6) {
    return 90;
  }
  // Other values (2,4,5,7) indicate the image is also flipped.  Hopefully we won't need to support those
  // -2: not jpeg
  // -1: not defined
  return 0;
};

/**
 * iOS photos often have the wrong orientation, and use exif data to give the rotation.
 * This method seems to work well, but we may want to replace it later with something that is more
 * general purpose so we can get all exif data.
 * We're also going to want the ability write exif data.
 */
export const photoOrientation = (
  file: File | Blob,
  callback: (orientationValue: number) => void,
) => {
  const reader = new FileReader();
  reader.onload = () => {
    if (!(reader.result instanceof ArrayBuffer)) {
      return callback(-1);
    }
    const view = new DataView(reader.result);
    if (view.getUint16(0, false) !== 0xffd8) {
      return callback(-2);
    }
    const length = view.byteLength;
    let offset = 2;
    while (offset < length) {
      if (view.getUint16(offset + 2, false) <= 8) return callback(-1);
      const marker = view.getUint16(offset, false);
      offset += 2;
      if (marker === 0xffe1) {
        offset += 2;
        if (view.getUint32(offset, false) !== 0x45786966) {
          return callback(-1);
        }

        const little = view.getUint16((offset += 6), false) === 0x4949;
        offset += view.getUint32(offset + 4, little);
        const tags = view.getUint16(offset, little);
        offset += 2;
        for (let i = 0; i < tags; i++) {
          if (view.getUint16(offset + i * 12, little) === 0x0112) {
            return callback(view.getUint16(offset + i * 12 + 8, little));
          }
        }
      }
      // eslint-disable-next-line no-bitwise
      else if ((marker & 0xff00) !== 0xff00) {
        break;
      } else {
        offset += view.getUint16(offset, false);
      }
    }
    return callback(-1);
  };
  reader.readAsArrayBuffer(file);
};

/**
 * If the image uploaded has wrong orientation we fix it so that the user can crop the image with proper orientation.
 * Since we are using canvas to rotate image, we copy exif data from original file to the rotated image.
 */
export const rotateImageIfNeeded = (imageFile: File, callback: (imageUrl: string) => void) => {
  photoOrientation(imageFile, orientationValue => {
    const rotationDegrees = orientationValueToDegrees(orientationValue);
    if (rotationDegrees === 0) {
      callback(URL.createObjectURL(imageFile));
    } else {
      // read original file and copy exif data to rotated image
      const reader = new FileReader();
      reader.onload = () => {
        const originalDataUrl = (reader.result && reader.result.toString()) || '';
        const mimeType = imageFile.type;
        const unrotatedUrl = URL.createObjectURL(imageFile);
        rotateImage(unrotatedUrl, rotationDegrees, mimeType, dataUrl => {
          URL.revokeObjectURL(unrotatedUrl);
          let finalDataUrl = dataUrl;
          try {
            finalDataUrl = restoreExif(originalDataUrl, finalDataUrl, mimeType);
          } catch (error) {
            Logging.warn('Cannot restore exif:', error);
          }
          const blob = new Blob([base64ToUint8Array(finalDataUrl)], { type: mimeType });
          callback(URL.createObjectURL(blob));
        });
      };
      reader.readAsDataURL(imageFile);
    }
  });
};

const restoreExifFromOriginal = (
  blob: Blob,
  croppedImageDataUrl: string,
  mimeType: string,
  callback: (dataUrl: string) => void,
) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = (reader.result && reader.result.toString()) || '';
    const restoredDataUrl = restoreExif(result, croppedImageDataUrl, mimeType);
    callback(restoredDataUrl);
  };
  reader.readAsDataURL(blob);
};

/**
 * Once we rotate an image and crop it, we should copy the exif info from original image to cropped image.
 * Since we are copying the exif data, we should rotate the cropped image back to original orientation.
 * Once this cropped image is submitted, media server will read the exif data and fix the orientation.
 */
export const restoreExifAndRotateIfNeeded = (
  originalImageUrl?: string,
  croppedImageDataUrl: string,
  mimeType: string,
  callback: (dataUrl: string) => void,
) => {
  if (!originalImageUrl) {
    callback(croppedImageDataUrl);
  } else {
    fetch(originalImageUrl, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return response;
      })
      .then(response => response.blob())
      .then(blob => {
        photoOrientation(blob, orientationValue => {
          const rotationDegrees = orientationValueToDegrees(orientationValue);
          if (rotationDegrees === 0) {
            restoreExifFromOriginal(blob, croppedImageDataUrl, mimeType, callback);
          } else {
            rotateImage(croppedImageDataUrl, -rotationDegrees, mimeType, dataUrl => {
              restoreExifFromOriginal(blob, dataUrl, mimeType, callback);
            });
          }
        });
      })
      .catch(e => {
        Logging.warn('Cannot restore exif:', e);
        callback(croppedImageDataUrl);
      });
  }
};

export const mediaType = (filename: string): MediaType => {
  if (supportedImageTypes.includes(fileExtentsion(filename))) {
    return 'photo';
  }
  if (supportedVideoTypes.includes(fileExtentsion(filename))) {
    return 'video';
  }
  return 'unknown';
};

/**
 * When media is uploaded it might not be processed immediately so a preview image might be shown to the user, which is low resolution.
 * In order to solve this problem we wait for 1 sec and check if the photo is processed by media upload server by looking at the status of the photo.
 * If UPLOADING is in the status that means photo is not processed yet.
 * Usually photos are processed in under 3sec but we retry up to maxRetry.
 *
 * @param mediaId
 * @param retryCounter
 */
export const checkMediaReady = (
  mediaId: number,
  retryCounter: number,
  maxRetry: number,
  // $FlowFixGraphql
  apolloClient: ApolloClient,
  callback: () => void,
) => {
  if (retryCounter > maxRetry) {
    callback();
    return;
  }

  setTimeout(() => {
    apolloClient
      .query({
        query: PhotoDetails,
        variables: { id: mediaId },
        fetchPolicy: 'network-only',
      })
      .then(response => {
        if (!response.data || !response.data.photos || !response.data.photos[0]) {
          checkMediaReady(mediaId, retryCounter + 1, maxRetry, apolloClient, callback);
          return;
        }

        const { statuses } = response.data.photos[0];

        if (!statuses || statuses.length === 0 || statuses.includes('UPLOADING')) {
          checkMediaReady(mediaId, retryCounter + 1, maxRetry, apolloClient, callback);
          return;
        }

        callback();
      });
  }, 1000);
};
